import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState } from 'recoil';
import { useLocation, useNavigate, useParams } from 'react-router';
import ChartSidebar from './ChartSidebar.tsx';
import ChartNode from './ChartNode.tsx';
import ModalForms from '../../../Shared/Modals/index.js';
import LoadingButton from '../../../Shared/Loading/index.tsx';
import { ReportActions } from '../../../../Services/_actions/Report/report.action.js';
import { reportTemplateDetailsAtom } from '../../../../Services/_states/Report/index.ts';

type ChartType = 'bar' | 'pie' | 'line' | 'table' | 'stacked' | 'area' | 'donut';

type NodeItem = {
  id: string;
  chartType: ChartType;
  serial: number;
  title?: string;
  grouping?: string;
  filter?: string;
  aggregation?: string | string[];
};

export default function CreateReportFormat() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const location = useLocation() as any;
  const { showSnackBar } = useSnackBar();
  const [templateReport, setTemplateReport] = useRecoilState(reportTemplateDetailsAtom);
  const [formId, setFormId] = useState < number | string > ('');
  const [formName, setFormName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [updating, isUpdating] = useState(false);
  const [saving, isSaving] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const actions = useRef(ReportActions()).current;

  const modifyChartAggregations = (chartArray) => {
    try {
      return chartArray.map(chart => {
        const modifiedChart = JSON.parse(JSON.stringify(chart));
        if (!modifiedChart?.collection) {
          modifiedChart.collection = "leads";
        }
        if (modifiedChart.aggregations && Array.isArray(modifiedChart.aggregations)) {
          modifiedChart.aggregations = modifiedChart.aggregations.map(aggregation => {
            const modifiedAggregation = {};

            // Process each property in aggregation
            for (const [key, value] of Object.entries(aggregation)) {
              if (!value) {
                modifiedAggregation[key] = "*";
              } else {
                modifiedAggregation[key] = value;
              }
            }

            return modifiedAggregation;
          });
        }
        return modifiedChart;
      });
    } catch (error) {
      console.error('Error modifying chart aggregations:', error);
      return chartArray;
    }
  }


  const buildPayload = () => {
    const modifiedCharts = modifyChartAggregations(templateReport?.charts ?? []);
    return {
      ...templateReport,
      charts: modifiedCharts,
      leads_form_id: formId,
      format: 'html',
    }
  };

  useEffect(() => {
    const { leads_form_id, leads_form_name } = location.state || {};
    setFormId(leads_form_id || '');
    setFormName(leads_form_name || '');
    if (!reportId) {
      setTemplateReport({ ...templateReport, leads_form_id: leads_form_id });
      if (!templateReport?.name?.trim()) {
        setNameDialogOpen(true);
      }
    } else {
      (async () => {
        isUpdating(true);
        try {
          const res = await actions.getReportTemplateDetails(reportId);
          if (!(res?.status === 200 || res?.data)) {
            showSnackBar('Failed to fetch template details', 'error');
            return;
          }
          const details = res?.data ?? {};
          setTemplateReport(details);
        } catch {
          showSnackBar('Failed to fetch template details', 'error');
        } finally {
          isUpdating(false);
        }
      })();
      setNameDialogOpen(false);
    }
  }, [location.state]);

  const handleUpdateTemplate = async () => {

    if (!templateReport?.name?.trim()) {
      setNameDialogOpen(true);
      return;
    }
    if (!templateReport?._id) {
      showSnackBar('No template to update', 'error');
      return;
    }
    const reportTemplateId = templateReport?._id;
    try {
      isUpdating(true);
      const payload = {
        ...buildPayload(templateReport),
      };
      const res = await actions.updateReportTemplate(reportTemplateId, payload);
      if (res?.status && res.status === 200) {
        showSnackBar('Template updated', 'success');
      } else {
        showSnackBar('Failed to update template', 'error');
      }
    } catch {
      showSnackBar('Failed to update template', 'error');
    } finally {
      isUpdating(false);
    }
  };
  const handleCreateTemplate = async () => {

    if (!templateReport?.name?.trim()) {
      setNameDialogOpen(true);
      return;
    }
    try {
      isUpdating(true);
      const payload = {
        ...buildPayload(templateReport),
      };
      const res = await actions.generateReportTemplates(payload);
      if (res?.status && res.status === 200) {
        setTemplateReport({ ...templateReport, _id: res?.data?.data?.template_id });
        showSnackBar('Template created', 'success');
      } else {
        showSnackBar('Failed to create template', 'error');
      }
    } catch {
      showSnackBar('Failed to create template', 'error');
    } finally {
      isUpdating(false);
    }
  };

  const handleSave = async () => {
    if (!templateReport?.name?.trim()) {
      setNameDialogOpen(true);
      return;
    }
    try {
      isSaving(true);
      const res = await actions.generateQuickReport(buildPayload(templateReport));
      if (res?.status && res.status === 200) {
        showSnackBar('Report saved successfully', 'success');
        navigate('/r/records/html-preview', { state: { name: templateReport?.name, report: res?.data } });
      } else {
        showSnackBar('Failed to save report', 'error');
      }
    } catch {
      showSnackBar('Failed to save report', 'error');
    } finally {
      isSaving(false);
    }
  };

  const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  return (
    <>
      <ModalForms
        show={nameDialogOpen}
        handleClose={() => setNameDialogOpen(false)}
        modalHeader={editingName ? 'Edit Report Name' : 'New Report'}
        size='sm'
        modalBody={
          <>
            <TextField
              autoFocus
              fullWidth
              size='small'
              label="Report name"
              value={templateReport?.name}
              onChange={(e) => setTemplateReport({ ...templateReport, name: e?.target?.value ?? "" })}
              margin="dense"
              placeholder="e.g., Comprehensive Lead Dashboard"
            />
            <TextField
              fullWidth
              label="Description"
              size='small'
              value={templateReport?.description ?? ""}
              onChange={(e) => setTemplateReport({ ...templateReport, description: e.target.value ?? '' })}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Selected Lead Form"
              size='small'
              value={formName || (formId ? String(formId) : '')}
              margin="dense"
              InputProps={{ readOnly: true }}
            />
          </>
        }
        modalFooter={
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button variant="contained" onClick={() => setNameDialogOpen(false)}>
              Continue
            </Button>
          </Box>
        }
      />

      <Box sx={{ display: 'flex', height: '85vh' }}>
        <Box
          sx={{
            width: 280,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <ChartSidebar />
        </Box>

        <Box sx={{ flex: 1, p: 2, bgcolor: '#f9f9f9' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">{templateReport?.name || 'Untitled Report'}</Typography>
              <IconButton size="small" onClick={() => setNameDialogOpen(true)}>
                <Edit />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={templateReport?.is_published ?? false}
                    onChange={(e) => setTemplateReport({ ...templateReport, is_published: e.target.checked })}
                    inputProps={{ 'aria-label': 'controlled' }}
                    size="small"
                  />
                }
                sx={{ m: 0 }}
                label={'Published'}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={templateReport?.save_as_template ?? false}
                    onChange={(e) => setTemplateReport({ ...templateReport, save_as_template: e.target.checked })}
                    inputProps={{ 'aria-label': 'controlled' }}
                    size="small"
                  />
                }
                sx={{ m: 0 }}
                label={'Template'}
              />
              {templateReport?._id && (
                <LoadingButton
                  buttonName="Preview"
                  onClickButton={handleSave}
                  isLoading={saving}
                />

              )}

              <LoadingButton
                buttonName={templateReport?._id ? "Update" : "Save"}
                onClickButton={templateReport?._id ? handleUpdateTemplate : handleCreateTemplate}
                isLoading={updating}
              />

            </Stack>
          </Stack>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              minHeight: 200,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const chartType = e.dataTransfer.getData('chartType') as ChartType;
              if (!chartType) return;
              const newNode: NodeItem = {
                id: crypto.randomUUID(),
                type: chartType,
                serial: (templateReport?.charts || [])?.length + 1,
                title: `${capitalize(chartType)} Chart`,
                filter: '',
              };
              setTemplateReport({ ...templateReport, charts: [...(templateReport?.charts || []), newNode] });
              // setNodes((prev) => [...prev, newNode]);
            }}
          >
            <ChartNode />
          </Box>
        </Box>
      </Box>
    </>
  );
}
